import React, { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { intl } from "LocaleWrapper";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@apollo/client";

import { FormGroup, showSnackbar } from "@cofano/cck";
import { Grid2 as Grid } from "@mui/material";

import { FormDialog } from "components/dialogs/Dialogs";
import { FormErrorMessage } from "components/form/FormErrorMessage";

import { usePermission } from "imports/Utils";

import LocationSelect from "view/locations/components/selects/LocationSelect";
import { voyageCallSchema, VoyageCallSchemaInput, VoyageCallSchemaOutput } from "view/voyages/utils/voyage.schema";

import VoyageCallTimesFormFields from "../form/VoyageCallTimesFormFields";

import { GeneralCallFragment } from "__generated__/view/calls/graphql/call.fragments.generated";
import { UpdateCallTimesAndLocationDocument } from "__generated__/view/calls/graphql/call.mutations.generated";
import { LocationWithGroupFragment } from "__generated__/view/locations/graphql/location.fragments.generated";

const VoyageCallUpdateTimesAndLocationDialog = ({
	open,
	onClose,
	call,
	refetch,
	isLocationEditable = false,
}: {
	open: boolean;
	onClose: () => void;
	call: Pick<GeneralCallFragment,
		"id" |
		"atn" |
		"dtn" |
		"ata" |
		"atd" |
		"ateo" |
		"atso" |
		"eta" |
		"etd" |
		"eteo" |
		"etso" |
		"pta" |
		"ptd"
	> & {
		location: LocationWithGroupFragment
	};
	refetch: () => void;
	isLocationEditable?: boolean;
}) => {
	const { isRTR } = usePermission();

	const { control, handleSubmit, formState, reset } = useForm<VoyageCallSchemaInput, unknown, VoyageCallSchemaOutput>({
		resolver: zodResolver(voyageCallSchema),
		defaultValues: {
			id: null,
			location: null,
			atn: null,
			dtn: null,
			ata: null,
			atd: null,
			ateo: null,
			atso: null,
			eta: null,
			etd: null,
			eteo: null,
			etso: null,
			pta: null,
			ptd: null,
		},
	});

	const [ updateCallTimesAndLocations, { loading } ] = useMutation(UpdateCallTimesAndLocationDocument);

	const save = (data: VoyageCallSchemaOutput) => {
		const { location, ...restData } = data;

		updateCallTimesAndLocations({
			variables: {
				input: {
					...restData,
					locationId: location.id,
				},
			},
		}).then(() => {
			onClose();
			refetch();
			showSnackbar(intl.formatMessage({ id: "SUCCESSFUL_SAVED" }), { variant: "success" });
		});
	};

	useEffect(() => {
		if (open) {
			reset({
				id: call.id,
				location: call.location,
				atn: call.atn ?? null,
				dtn: call.dtn ?? null,
				ata: call.ata ?? null,
				atd: call.atd ?? null,
				ateo: call.ateo ?? null,
				atso: call.atso ?? null,
				eta: call.eta ?? null,
				etd: call.etd ?? null,
				eteo: call.eteo ?? null,
				etso: call.etso ?? null,
				pta: call.pta ?? null,
				ptd: call.ptd ?? null,
			});
		}
	}, [ open ]);

	return (
		<FormDialog
			loading={loading}
			open={open}
			onClose={onClose}
			title={intl.formatMessage({ id: "EDIT_CALL_TIMES" })}
			form="callTimesForm"
			maxWidth="md">
			<form onSubmit={handleSubmit(save)} id="callTimesForm">
				<Grid container spacing={3}>
					{isLocationEditable && (
						<Grid size={6}>
							<FormGroup label={intl.formatMessage({ id: "LOCATION" })} required>
								<Controller
									name="location"
									control={control}
									render={({ field }) => (
										<React.Fragment>
											<LocationSelect {...field} disabled={!isRTR} />
											<FormErrorMessage name={field.name} errors={formState.errors} />
										</React.Fragment>
									)}
								/>
							</FormGroup>
						</Grid>
					)}

					<Grid size={12}>
						<VoyageCallTimesFormFields control={control} />
					</Grid>
				</Grid>
			</form>
		</FormDialog>
	);
};

export default VoyageCallUpdateTimesAndLocationDialog;
